import {RuleSeverityBadge} from '@github-ui/code-scanning-shared/components/RuleSeverityBadge'
import {AutomatedReviewThreadComment} from '@github-ui/diff-lines/automated-review-thread-comment'
import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'
import {type FC, useState} from 'react'
import {AnnotationFindingDetails} from './AnnotationFindingDetails'
import {
  getEntityComment,
  getEntityCompositeID,
  getEntityFileLocation,
  getEntitySeverity,
  getEntitySource,
} from './entity-helpers'
import styles from './Finding.module.css'
import {FindingActions} from './FindingActions'
import type {FindingEntity} from './types'

type FindingProps = FindingEntity & {
  basePath: string
  dismissed?: boolean
  onDismiss?: (entityCompositeId: string) => void
  onReopen?: (entityCompositeId: string) => void
}

export const Finding: FC<FindingProps> = ({onDismiss, onReopen, dismissed, basePath, ...finding}) => {
  const [showDetails, setShowDetails] = useState(false)
  const severity = getEntitySeverity(finding)
  const source = getEntitySource(finding)
  const fileLocation = getEntityFileLocation(finding)

  return (
    <div className={styles.Finding}>
      <button
        type="button"
        className={styles.header}
        onClick={() => setShowDetails(prev => !prev)}
        aria-expanded={showDetails}
      >
        <span className={styles.chevron}>
          {showDetails ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
        </span>
        <span className={styles.headerContent}>
          <span className={styles.title}>{finding.entity.title}</span>
          <span className={styles.meta}>
            {severity && <RuleSeverityBadge severity={severity} />}
            {source && (
              <>
                <span className={styles.metaSeparator}>{'\u00B7'}</span>
                <span>{source}</span>
              </>
            )}
            {fileLocation && (
              <>
                <span className={styles.metaSeparator}>{'\u00B7'}</span>
                <span className={styles.fileLocation}>{fileLocation}</span>
              </>
            )}
          </span>
        </span>
      </button>
      {showDetails && (
        <div className={styles.expandedContent}>
          {finding.entityType === 'automated_review_comment' ? (
            <div className={styles.section}>
              <AutomatedReviewThreadComment
                basePath={basePath}
                comment={getEntityComment(finding)}
                threadId={String(finding.entity.pullRequestReviewCommentId ?? finding.entity.id)}
              />
            </div>
          ) : (
            <AnnotationFindingDetails finding={finding} />
          )}
          <div className={styles.footer}>
            <FindingActions
              onDismiss={() => onDismiss?.(getEntityCompositeID(finding))}
              onReopen={() => onReopen?.(getEntityCompositeID(finding))}
              dismissed={dismissed}
              basePath={basePath}
              finding={finding}
            />
          </div>
        </div>
      )}
    </div>
  )
}
